import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import {
  PhoneIphone as MobileIcon,
  DirectionsCar as CarIcon,
  PersonPin as DriverIcon,
  Home as HostIcon,
  Payment as PaymentIcon,
  Star as RatingIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { liveOperationsService } from '@/services/liveOperationsService';

interface MobileActivity {
  id: string;
  type: 'booking' | 'payment' | 'rating' | 'registration' | 'verification' | 'driver_status' | 'error';
  action: string;
  user: {
    id: string;
    name: string;
    role: 'renter' | 'host' | 'driver';
  };
  details: any;
  timestamp: string;
  platform: 'ios' | 'android';
  appVersion?: string;
}

const MobileActivityFeed: React.FC = () => {
  const theme = useTheme();
  const [activities, setActivities] = useState<MobileActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to real-time mobile activity updates
  // const { isConnected } = useWebSocket({
  //   autoConnect: true,
  //   onMessage: (message) => {
  //     // Handle real-time activity updates
  //   }
  // });

  useEffect(() => {
    // Load initial activities from real API
    fetchRecentActivities();

    // Set up polling for real-time updates
    const interval = setInterval(fetchRecentActivities, 15000); // Every 15 seconds
    
    return () => clearInterval(interval);
  }, []);

  const fetchRecentActivities = async () => {
    setLoading(true);
    setError(null);
    try {
      
      // Get real mobile activity from live operations API
      const mobileActivityData = await liveOperationsService.getMobileActivity();
      
      // Transform to component format
      const transformedActivities: MobileActivity[] = mobileActivityData.map(activity => ({
        id: activity.id,
        type: activity.type,
        action: activity.action,
        user: activity.user,
        details: activity.details,
        timestamp: activity.timestamp,
        platform: activity.platform,
        appVersion: activity.appVersion
      }));
      
      setActivities(transformedActivities);
      
    } catch (err) {
      console.error('❌ Error fetching mobile activities:', err);
      setError('Failed to load mobile activities');
      
      // Keep existing activities if API fails
      if (activities.length === 0) {
        // Only show fallback if no existing data
        setActivities([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (activity: MobileActivity) => {
    switch (activity.type) {
      case 'booking':
        return <CarIcon />;
      case 'payment':
        return <PaymentIcon />;
      case 'rating':
        return <RatingIcon />;
      case 'registration':
        return activity.user.role === 'host' ? <HostIcon /> : <DriverIcon />;
      case 'verification':
        return <DriverIcon />;
      case 'driver_status':
        return <LocationIcon />;
      case 'error':
        return <WarningIcon color="error" />;
      default:
        return <MobileIcon />;
    }
  };

  const getActivityColor = (type: MobileActivity['type']) => {
    switch (type) {
      case 'booking':
        return theme.palette.primary.main;
      case 'payment':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'driver_status':
        return theme.palette.info.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'renter':
        return 'primary';
      case 'host':
        return 'secondary';
      case 'driver':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatActivityTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return format(date, 'MMM d, HH:mm');
  };

  const renderActivityDetails = (activity: MobileActivity) => {
    switch (activity.type) {
      case 'booking':
        return `${activity.details.vehicleName} for ${activity.details.duration}`;
      case 'payment':
        return `₦${activity.details.amount.toLocaleString()} via ${activity.details.method}`;
      case 'driver_status':
        return activity.details.location;
      case 'registration':
        return `${activity.details.vehicleCount} vehicles in ${activity.details.location}`;
      case 'verification':
        return `Uploaded ${activity.details.documentType.replace('_', ' ')}`;
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, height: 400, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MobileIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Mobile App Activity
          </Typography>
          <Chip 
            label="LIVE" 
            size="small" 
            color="error" 
            sx={{ 
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 },
              }
            }} 
          />
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchRecentActivities} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List dense>
          {activities.length === 0 ? (
            <ListItem>
              <ListItemText 
                primary="No recent mobile activity"
                secondary="Activities will appear here as users interact with the mobile app"
              />
            </ListItem>
          ) : (
            activities.map((activity) => (
              <ListItem
                key={activity.id}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                  border: `1px solid ${alpha(getActivityColor(activity.type), 0.2)}`,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  }
                }}
              >
                <ListItemIcon sx={{ color: getActivityColor(activity.type), minWidth: 40 }}>
                  {getActivityIcon(activity)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {activity.action}
                      </Typography>
                      <Chip 
                        label={activity.user.role} 
                        size="small" 
                        color={getRoleColor(activity.user.role) as any}
                        sx={{ height: 20 }}
                      />
                      <Chip 
                        label={activity.platform} 
                        size="small" 
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {activity.user.name} • {renderActivityDetails(activity)}
                      </Typography>
                    </Box>
                  }
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatActivityTime(activity.timestamp)}
                  </Typography>
                  {activity.appVersion && (
                    <Chip 
                      label={`v${activity.appVersion}`} 
                      size="small" 
                      sx={{ height: 16, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </ListItem>
            ))
          )}
        </List>
      </Box>
    </Paper>
  );
};

export default MobileActivityFeed;